/* eslint-disable no-unsafe-optional-chaining */
import { useMemo, useState } from 'react';
import { Card, CardBody, CardFooter } from '../../../../../components/card/card';
import { CardHeader } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import NoData from '@components/NoData/NoData';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { getPagedData, setFixedFilters, setPagination } from '@store/DynamicSlice/dynamicTableSlice';
import { DynamicPagination } from '@Helper/DynamicFilter/DynamicPagination';

interface Props {
  tableKey: string;
  data: any[];
  isPendingAdd: (item: any) => boolean;
  onRemoveItems: (items: any[]) => void;
  onRemoveAllVisible: () => void;
}

const CARD_HEIGHT = 900;

const NULL_FIXED = {
  itemCode: null,
  name: null,
  categoryId: null,
  unitOfMeasure: null,
  brandId: null,
  barcode: null,
  itemType: null
};

const FILTER_OPTIONS = [
  { value: 'name', label: 'الاسم' },
  { value: 'itemCode', label: 'كود الصنف' },
  { value: 'barcode', label: 'الباركود' },
  { value: 'itemType', label: 'نوع الصنف' }
] as const;

type FilterKey = (typeof FILTER_OPTIONS)[number]['value'];

const getKey = (item: any) => String(item?.id ?? '');

const ItemsInInventory = ({ tableKey, data, isPendingAdd, onRemoveItems, onRemoveAllVisible }: Props) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const table = useAppSelector(state => state.dynamicTable[tableKey]);
  const loading = table?.loading ?? false;
  const error = table?.error ?? null;
  const pageSize = table?.pageSize ?? 20;

  const [filterKey, setFilterKey] = useState<FilterKey>('name');
  const [filterText, setFilterText] = useState('');

  const [selectedMap, setSelectedMap] = useState<Record<string, any>>({});

  const currentData = data ?? [];
  const selectedItems = useMemo(() => Object.values(selectedMap), [selectedMap]);

  const isAllVisibleSelected = useMemo(() => {
    if (!currentData.length) return false;
    return currentData.every(it => !!selectedMap[getKey(it)]);
  }, [currentData, selectedMap]);

  const toggleSelect = (item: any) => {
    const k = getKey(item);
    if (!k) return;
    setSelectedMap(prev => {
      const next = { ...prev };
      if (next[k]) delete next[k];
      else next[k] = item;
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedMap(prev => {
      const next = { ...prev };
      currentData.forEach(it => {
        const k = getKey(it);
        if (k) next[k] = it;
      });
      return next;
    });
  };

  const deselectAllVisible = () => {
    setSelectedMap(prev => {
      const next = { ...prev };
      currentData.forEach(it => {
        const k = getKey(it);
        if (k) delete next[k];
      });
      return next;
    });
  };

  const handleRemoveSelected = () => {
    if (!selectedItems.length) return;
    onRemoveItems(selectedItems);
    setSelectedMap({});
  };

  const applySearch = () => {
    const text = filterText.trim();

    dispatch(
      setFixedFilters({
        tableKey,
        fixedFilters: {
          ...NULL_FIXED,
          [filterKey]: text ? text : null
        }
      })
    );

    dispatch(setPagination({ tableKey, pageIndex: 0, pageSize }));
    dispatch(getPagedData({ tableKey }));
  };

  const clearSearch = () => {
    setFilterText('');
    setFilterKey('name');

    dispatch(setFixedFilters({ tableKey, fixedFilters: { ...NULL_FIXED } }));
    dispatch(setPagination({ tableKey, pageIndex: 0, pageSize }));
    dispatch(getPagedData({ tableKey }));
  };

  return (
    <div className='col-lg-6 mb-1 d-flex' style={{ height: CARD_HEIGHT }}>
      <Card classNeme='w-100 d-flex flex-column' style={{ height: '100%' }}>
        <CardHeader className='fw-bold small bg-info bg-opacity-25'>
          <h6 className='m-1'>الأصنـاف المعيّنة في المخزن</h6>
        </CardHeader>

        <CardBody className='d-flex flex-column flex-grow-1' style={{ minHeight: 0 }}>
          {/* بحث ثابت */}
          <div className='d-flex gap-2 mb-3'>
            <select
              className='form-select'
              style={{ maxWidth: 180 }}
              value={filterKey}
              onChange={e => setFilterKey(e.target.value as FilterKey)}
            >
              {FILTER_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <input
              className='form-control'
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
              placeholder='اكتب قيمة الفلتر...'
              onKeyDown={e => {
                if (e.key === 'Enter') applySearch();
              }}
            />

            <button className='btn btn-theme' type='button' onClick={applySearch} disabled={loading}>
              <i className='bi bi-search me-1'></i> بحث
            </button>

            <button className='btn btn-outline-secondary' type='button' onClick={clearSearch} disabled={loading}>
              مسح
            </button>
          </div>

          {error ? <div className='alert alert-danger py-1 small mb-2'>{String(error)}</div> : null}

          {loading ? (
            <div className='flex-grow-1 d-flex justify-content-center align-items-center' style={{ minHeight: 0 }}>
              <div className='spinner-border spinner-border-sm text-theme me-2'></div>
              <span className='small text-muted'>جاري تحميل الأصناف...</span>
            </div>
          ) : (
            <>
              <div className='flex-grow-1' style={{ minHeight: 0 }}>
                {currentData.length ? (
                  <div className='table-responsive h-100' style={{ overflowY: 'auto' }}>
                    <table className='table table-hover mb-0'>
                      <tbody>
                        {currentData.map((item, idx) => {
                          const k = getKey(item);
                          const selected = !!selectedMap[k];
                          const pending = isPendingAdd(item);

                          return (
                            <tr
                              key={k || idx}
                              style={{ cursor: 'pointer' }}
                              onClick={() => toggleSelect(item)}
                              className={selected ? 'table-active' : ''}
                            >
                              <td className='align-middle'>
                                <div className='d-flex justify-content-between'>
                                  <div>
                                    <span className='fw-bold'>{i18n.language === 'en' ? item?.name : item?.name2}</span>
                                    {pending ? <span className='badge bg-warning text-dark ms-2'>قيد الإضافة</span> : null}
                                    {item?.itemCode ? <div className='small text-muted'>{item.itemCode}</div> : null}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className='h-100 d-flex align-items-center justify-content-center'>
                    <NoData />
                  </div>
                )}
              </div>

              <div className='mt-2'>
                <DynamicPagination tableKey={tableKey} maxVisiblePages={1} />
              </div>
            </>
          )}
        </CardBody>

        <CardFooter className='d-flex justify-content-between align-items-center'>
          <div className='d-flex gap-2'>
            <button
              onClick={() => (isAllVisibleSelected ? deselectAllVisible() : selectAllVisible())}
              className='btn btn-outline-danger'
              disabled={!currentData.length || loading}
            >
              {isAllVisibleSelected ? t('UnselectAll') : t('SelectAll')}
            </button>

            <button onClick={handleRemoveSelected} className='btn btn-danger' disabled={!selectedItems.length || loading}>
              <i className='fa fa-trash me-2'></i>
              {t('RemoveSelected')}
            </button>
          </div>

          <button onClick={onRemoveAllVisible} className='btn btn-danger' disabled={!currentData.length || loading}>
            <i className='fa fa-trash-alt me-2'></i>
            {t('RemoveAllVisible')}
          </button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ItemsInInventory;
